import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  computeEexQuoteBook,
  eexQuoteMonthHours,
  eexQuoteMonthRange,
  eexQuoteRowHasMarketInputs,
  eexQuoteRowIsActive,
  type EexQuoteRowInput,
} from "@chro/shared";
import {
  Copy,
  CheckCircle2,
  FilePlus2,
  Library,
  Layers3,
  PanelRightClose,
  PanelRightOpen,
  Save,
  SlidersHorizontal,
  Sparkles,
  TriangleAlert,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, ApiError } from "../lib/api";
import {
  EUR_RON_PAIR,
  isoToday,
  normalizeFxCurveDetail,
  quoteFxCurveSnapshot,
  type FxCurveDetail,
} from "../lib/eex-fx";
import { cn } from "../lib/utils";
import { useAuth } from "../stores/auth";
import { useActiveCompany } from "../stores/active-company";
import { useEexRole } from "../components/eex/use-eex-role";
import { QuoteGrid } from "../components/eex/quotes/QuoteGrid";
import { QuoteSidebar, type QuoteSidePanel } from "../components/eex/quotes/QuoteSidebar";
import { SaveWorkspaceDialog, ShortcutDialog } from "../components/eex/quotes/QuoteDialogs";
import { QuoteMarketSnapshotDialog } from "../components/eex/quotes/QuoteMarketSnapshotDialog";
import {
  DEFAULT_QUOTE_LAYOUT,
  createDefaultQuoteSnapshot,
  normalizeQuoteSnapshot,
  quoteFxOverrideMonths,
  quoteHasFrozenMarketSource,
  quoteLocalDraftKey,
  resetQuoteConfirmationAfterEconomicEdit,
  type QuoteComponent,
  type QuoteSnapshot,
  type QuoteVisibility,
  type QuoteWorkspaceKind,
  type QuoteWorkspaceRecord,
  type QuoteWorkspaceSummary,
} from "../components/eex/quotes/model";
import { desktopOnlyRoute } from "../components/ui/mobile-availability-page";

export const Route = createFileRoute("/eex/quotes")({
  component: desktopOnlyRoute(EexQuotesPage),
});

type CurrentWorkspace = Omit<QuoteWorkspaceRecord, "snapshot">;

interface SaveDialogState {
  kind: QuoteWorkspaceKind;
  initialName: string;
  initialVisibility: QuoteVisibility;
  sourceWorkspaceId: string | null;
}

interface LocalDraft {
  dirty: boolean;
  snapshot: unknown;
  sourceWorkspaceId?: string | null;
  savedAt: string;
}

function EexQuotesPage() {
  const queryClient = useQueryClient();
  const { canWrite, isAdmin } = useEexRole();
  const user = useAuth((state) => state.user);
  const activeCompanyId = useActiveCompany((state) => state.activeCompanyId);
  const [snapshot, setSnapshot] = useState<QuoteSnapshot>(() => createDefaultQuoteSnapshot());
  const snapshotRef = useRef(snapshot);
  const [current, setCurrent] = useState<CurrentWorkspace | null>(null);
  const currentRef = useRef<CurrentWorkspace | null>(current);
  const [marketSourceWorkspaceId, setMarketSourceWorkspaceId] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [sidePanel, setSidePanel] = useState<QuoteSidePanel | null>(null);
  const [selectedMonths, setSelectedMonths] = useState<string[]>([]);
  const [saveDialog, setSaveDialog] = useState<SaveDialogState | null>(null);
  const [shortcutOpen, setShortcutOpen] = useState(false);
  const [marketSnapshotOpen, setMarketSnapshotOpen] = useState(false);
  const [status, setStatus] = useState<{ tone: "ok" | "error" | "muted"; text: string } | null>(null);
  const [loadingWorkspaceId, setLoadingWorkspaceId] = useState<string | null>(null);
  const undoStack = useRef<QuoteSnapshot[]>([]);
  const redoStack = useRef<QuoteSnapshot[]>([]);
  const [historyVersion, setHistoryVersion] = useState(0);
  const hydratedDraft = useRef(false);
  const awaitingFreshFx = useRef(false);
  const sheetGeneration = useRef(0);
  const freshFxGeneration = useRef(0);
  const workspaceLoadGeneration = useRef<number | null>(null);
  const companyScopeId = user?.role === "global_admin" ? activeCompanyId : user?.companyId ?? null;
  const localKey = quoteLocalDraftKey(companyScopeId, user?.userId ?? null);
  const latestFxAsOf = isoToday();

  useEffect(() => {
    snapshotRef.current = snapshot;
  }, [snapshot]);

  useEffect(() => {
    currentRef.current = current;
  }, [current]);

  const replaceSnapshot = useCallback((next: QuoteSnapshot, options?: { history?: boolean; dirty?: boolean }) => {
    sheetGeneration.current++;
    hydratedDraft.current = true;
    const previous = snapshotRef.current;
    if (options?.history !== false) {
      undoStack.current.push(previous);
      if (undoStack.current.length > 100) undoStack.current.shift();
      redoStack.current = [];
      setHistoryVersion((value) => value + 1);
    } else if (redoStack.current.length > 0) {
      redoStack.current = [];
      setHistoryVersion((value) => value + 1);
    }
    snapshotRef.current = next;
    setSnapshot(next);
    if (options?.dirty !== false) setDirty(true);
  }, []);

  const replaceEconomicSnapshot = useCallback((next: QuoteSnapshot, options?: { history?: boolean }) => {
    replaceSnapshot(resetQuoteConfirmationAfterEconomicEdit(next), options);
  }, [replaceSnapshot]);

  const resetHistory = useCallback(() => {
    undoStack.current = [];
    redoStack.current = [];
    setHistoryVersion((value) => value + 1);
  }, []);

  const undo = useCallback(() => {
    const previous = undoStack.current.pop();
    if (!previous) return;
    sheetGeneration.current++;
    hydratedDraft.current = true;
    redoStack.current.push(snapshotRef.current);
    snapshotRef.current = previous;
    setSnapshot(previous);
    setDirty(true);
    setHistoryVersion((value) => value + 1);
  }, []);

  const redo = useCallback(() => {
    const next = redoStack.current.pop();
    if (!next) return;
    sheetGeneration.current++;
    hydratedDraft.current = true;
    undoStack.current.push(snapshotRef.current);
    snapshotRef.current = next;
    setSnapshot(next);
    setDirty(true);
    setHistoryVersion((value) => value + 1);
  }, []);

  const latestFxQuery = useQuery({
    queryKey: ["eex-fx-latest", companyScopeId, EUR_RON_PAIR, latestFxAsOf],
    enabled: companyScopeId != null,
    retry: false,
    queryFn: async (): Promise<FxCurveDetail | null> => {
      try {
        return normalizeFxCurveDetail(
          await api<unknown>(`/eex/fx/curves/latest?pair=${encodeURIComponent(EUR_RON_PAIR)}&asOf=${latestFxAsOf}`)
        );
      } catch (error) {
        if (error instanceof ApiError && error.status === 404) return null;
        throw error;
      }
    },
  });

  useEffect(() => {
    hydratedDraft.current = false;
    freshFxGeneration.current = ++sheetGeneration.current;
    awaitingFreshFx.current = false;
    workspaceLoadGeneration.current = null;
    setLoadingWorkspaceId(null);
    const fresh = createDefaultQuoteSnapshot(null);
    snapshotRef.current = fresh;
    setSnapshot(fresh);
    setCurrent(null);
    setMarketSourceWorkspaceId(null);
    setDirty(false);
    setStatus(null);
    resetHistory();
    let recovered = false;
    try {
      const raw = window.localStorage.getItem(localKey);
      if (raw) {
        const draft = JSON.parse(raw) as LocalDraft;
        const normalized = draft.dirty ? normalizeQuoteSnapshot(draft.snapshot) : null;
        if (normalized) {
          snapshotRef.current = normalized;
          setSnapshot(normalized);
          setMarketSourceWorkspaceId(
            normalized.schemaVersion === 2 && isUuid(draft.sourceWorkspaceId)
              ? draft.sourceWorkspaceId
              : null
          );
          setDirty(true);
          setStatus({ tone: "muted", text: `Recovered the local draft saved ${formatTimestamp(draft.savedAt)}.` });
          recovered = true;
        }
      }
    } catch {
      // A malformed local draft should never block the quote page.
    }
    if (recovered || companyScopeId == null) {
      hydratedDraft.current = true;
    } else {
      // Do not autosave the temporary blank sheet while the confirmed central
      // curve is loading. A cached query also runs this companion effect because
      // localKey is one of its dependencies.
      awaitingFreshFx.current = true;
    }
  }, [companyScopeId, localKey, resetHistory]);

  useEffect(() => {
    if (!awaitingFreshFx.current || latestFxQuery.isFetching || latestFxQuery.isPending) return;
    if (freshFxGeneration.current !== sheetGeneration.current) {
      awaitingFreshFx.current = false;
      hydratedDraft.current = true;
      if (!currentRef.current) setStatus({ tone: "muted", text: "Kept the current sheet after the FX request finished." });
      return;
    }
    const curve = latestFxQuery.error ? null : latestFxQuery.data ?? null;
    const next = createDefaultQuoteSnapshot(curve ? quoteFxCurveSnapshot(curve) : null);
    snapshotRef.current = next;
    setSnapshot(next);
    setCurrent(null);
    setMarketSourceWorkspaceId(null);
    setDirty(false);
    resetHistory();
    if (latestFxQuery.isError) {
      setStatus({ tone: "error", text: `Could not load the confirmed central FX curve. This fresh sheet has blank FX inputs: ${errorMessage(latestFxQuery.error)}` });
    } else if (curve) {
      setStatus({ tone: "muted", text: `Applied confirmed EUR/RON curve ${curve.curveDate} · version ${curve.version} · ${curve.source}.` });
    } else {
      setStatus({ tone: "muted", text: "No confirmed EUR/RON curve is available. This fresh sheet has blank FX inputs." });
    }
    awaitingFreshFx.current = false;
    hydratedDraft.current = true;
  }, [latestFxQuery.data, latestFxQuery.error, latestFxQuery.isError, latestFxQuery.isFetching, latestFxQuery.isPending, localKey, resetHistory]);

  useEffect(() => {
    if (!hydratedDraft.current) return;
    const timer = window.setTimeout(() => {
      const draft: LocalDraft = {
        dirty,
        snapshot,
        sourceWorkspaceId: snapshot.schemaVersion === 2 ? marketSourceWorkspaceId : null,
        savedAt: new Date().toISOString(),
      };
      window.localStorage.setItem(localKey, JSON.stringify(draft));
    }, 400);
    return () => window.clearTimeout(timer);
  }, [dirty, localKey, marketSourceWorkspaceId, snapshot]);

  const book = useMemo(
    () => computeEexQuoteBook(snapshot.rows, snapshot.settings),
    [snapshot.rows, snapshot.settings]
  );
  const confirmationIssue = useMemo(() => quoteConfirmationIssue(snapshot), [snapshot]);
  const fxOverrideMonths = useMemo(() => quoteFxOverrideMonths(snapshot), [snapshot]);
  const mainQuoteReady =
    snapshot.metadata.inputsConfirmed &&
    confirmationIssue == null &&
    snapshot.rows.some(eexQuoteRowIsActive);

  const historyState = useMemo(
    () => ({
      canUndo: undoStack.current.length > 0,
      canRedo: redoStack.current.length > 0,
    }),
    [historyVersion]
  );

  const workspacesQuery = useQuery({
    queryKey: ["eex-quotes", companyScopeId],
    queryFn: () => api<{ workspaces: QuoteWorkspaceSummary[] }>("/eex/quotes"),
  });
  const workspaces = workspacesQuery.data?.workspaces ?? [];

  const marketSnapshotAvailability = useQuery({
    queryKey: ["eex-quote-market-snapshot-availability", companyScopeId],
    enabled: companyScopeId != null && canWrite,
    retry: false,
    queryFn: () => api<{ available: boolean; reason?: string }>("/eex/quotes/market-snapshot/availability"),
  });

  useEffect(() => {
    if (workspacesQuery.isError) {
      setStatus({ tone: "error", text: `Could not load the quote library: ${errorMessage(workspacesQuery.error)}` });
    }
  }, [workspacesQuery.error, workspacesQuery.isError]);

  const createMutation = useMutation({
    onMutate: () => { sheetGeneration.current++; },
    mutationFn: (value: { kind: QuoteWorkspaceKind; name: string; visibility: QuoteVisibility; snapshot: QuoteSnapshot; sourceWorkspaceId: string | null }) =>
      api<{ workspace: QuoteWorkspaceRecord }>("/eex/quotes", {
        method: "POST",
        body: JSON.stringify({
          kind: value.kind,
          name: value.name,
          visibility: value.visibility,
          snapshot: value.snapshot,
          ...(value.snapshot.schemaVersion === 2 && value.sourceWorkspaceId
            ? { sourceWorkspaceId: value.sourceWorkspaceId }
            : {}),
        }),
      }),
    onSuccess: ({ workspace }, submitted) => {
      if (workspace.kind === "preset") {
        setSaveDialog(null);
        setStatus({ tone: "ok", text: `Preset “${workspace.name}” saved as version ${workspace.version}. The active quote remains open.` });
        void queryClient.invalidateQueries({ queryKey: ["eex-quotes"] });
        return;
      }
      const normalized = normalizeQuoteSnapshot(workspace.snapshot);
      const unchangedSinceSubmit = snapshotRef.current === submitted.snapshot;
      if (normalized && unchangedSinceSubmit) {
        snapshotRef.current = normalized;
        setSnapshot(normalized);
      }
      setCurrent(summaryOf(workspace));
      setMarketSourceWorkspaceId(unchangedSinceSubmit ? workspace.id : null);
      setDirty(!unchangedSinceSubmit);
      if (unchangedSinceSubmit) resetHistory();
      setSaveDialog(null);
      setStatus({ tone: "ok", text: `${workspace.kind === "quote" ? "Quote" : "Preset"} “${workspace.name}” saved as version ${workspace.version}.` });
      void queryClient.invalidateQueries({ queryKey: ["eex-quotes"] });
    },
  });

  const updateMutation = useMutation({
    onMutate: () => { sheetGeneration.current++; },
    mutationFn: (value: { workspace: CurrentWorkspace; snapshot: QuoteSnapshot }) =>
      api<{ workspace: QuoteWorkspaceRecord }>(`/eex/quotes/${value.workspace.id}`, {
        method: "PATCH",
        body: JSON.stringify({
          expectedVersion: value.workspace.version,
          name: value.workspace.name,
          visibility: value.workspace.visibility,
          snapshot: value.snapshot,
        }),
    }),
    onSuccess: ({ workspace }, submitted) => {
      const stillBoundToSavedWorkspace = currentRef.current?.id === submitted.workspace.id;
      if (stillBoundToSavedWorkspace) setCurrent(summaryOf(workspace));
      const unchangedSinceSubmit = snapshotRef.current === submitted.snapshot;
      const normalized = normalizeQuoteSnapshot(workspace.snapshot);
      if (stillBoundToSavedWorkspace && unchangedSinceSubmit && normalized) {
        snapshotRef.current = normalized;
        setSnapshot(normalized);
      }
      if (stillBoundToSavedWorkspace) setDirty(!unchangedSinceSubmit);
      if (stillBoundToSavedWorkspace) {
        setMarketSourceWorkspaceId(unchangedSinceSubmit ? workspace.id : null);
      }
      setStatus({ tone: "ok", text: !stillBoundToSavedWorkspace
        ? `Saved “${workspace.name}” · version ${workspace.version}. The current sheet was left unchanged.`
        : unchangedSinceSubmit
          ? `Saved “${workspace.name}” · version ${workspace.version}.`
          : `Saved “${workspace.name}” · version ${workspace.version}. Newer edits remain unsaved.` });
      void queryClient.invalidateQueries({ queryKey: ["eex-quotes"] });
    },
    onError: (error) => {
      const apiError = error instanceof ApiError ? error : null;
      const stale = apiError?.body && typeof apiError.body === "object" && "code" in apiError.body && apiError.body.code === "stale_version";
      setStatus({ tone: "error", text: stale ? "This workspace changed in another tab. Re-open it from the library, or save this draft under a new name." : errorMessage(error) });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (workspace: QuoteWorkspaceSummary) => api<{ ok: true }>(`/eex/quotes/${workspace.id}?expectedVersion=${workspace.version}`, { method: "DELETE" }),
    onSuccess: (_, workspace) => {
      if (currentRef.current?.id === workspace.id) {
        setCurrent(null);
        setMarketSourceWorkspaceId(null);
        setDirty(true);
      }
      setStatus({ tone: "muted", text: `Deleted “${workspace.name}”. The open sheet remains available as an unsaved draft.` });
      void queryClient.invalidateQueries({ queryKey: ["eex-quotes"] });
    },
    onError: (error) => setStatus({ tone: "error", text: errorMessage(error) }),
  });

  const savePending = createMutation.isPending || updateMutation.isPending;

  const canManageCurrent = Boolean(
    current && canWrite && (isAdmin || current.ownerUserId === user?.userId)
  );

  const saveCurrent = useCallback(() => {
    if (!canWrite || createMutation.isPending || updateMutation.isPending) return;
    if (current && canManageCurrent) {
      updateMutation.mutate({ workspace: current, snapshot: snapshotRef.current });
      return;
    }
    setSaveDialog({
      kind: current?.kind ?? "quote",
      initialName: current ? `Copy of ${current.name}` : snapshotRef.current.metadata.title,
      initialVisibility: current?.visibility ?? "private",
      sourceWorkspaceId: snapshotRef.current.schemaVersion === 2 ? marketSourceWorkspaceId : null,
    });
  }, [canManageCurrent, canWrite, createMutation.isPending, current, marketSourceWorkspaceId, updateMutation]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== "s") return;
      event.preventDefault();
      saveCurrent();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [saveCurrent]);

  const loadWorkspace = useCallback(async (workspace: QuoteWorkspaceSummary, mode: "open" | "apply" | "duplicate") => {
    if (savePending) return;
    if (dirty && !window.confirm(`${mode === "apply" ? "Apply this preset" : "Open this workspace"}? The current unsaved sheet will be replaced.`)) return;
    const generation = ++sheetGeneration.current;
    awaitingFreshFx.current = false;
    hydratedDraft.current = true;
    workspaceLoadGeneration.current = generation;
    setLoadingWorkspaceId(workspace.id);
    try {
      const result = await api<{ workspace: QuoteWorkspaceRecord }>(`/eex/quotes/${workspace.id}`);
      if (generation !== sheetGeneration.current) return;
      const normalized = normalizeQuoteSnapshot(result.workspace.snapshot);
      if (!normalized) throw new Error("saved workspace uses an unsupported quote schema");
      if (mode === "open") {
        snapshotRef.current = normalized;
        setSnapshot(normalized);
        setCurrent(summaryOf(result.workspace));
        setMarketSourceWorkspaceId(normalized.schemaVersion === 2 ? workspace.id : null);
        setDirty(false);
        resetHistory();
        setStatus({ tone: "muted", text: `Opened “${workspace.name}” · version ${result.workspace.version}.` });
      } else {
        const fresh = {
          ...normalized,
          metadata: {
            ...normalized.metadata,
            title: mode === "duplicate" ? `Copy of ${workspace.name}` : `${workspace.name} quote`,
            counterparty: mode === "apply" ? "" : normalized.metadata.counterparty,
            notes: mode === "apply" ? "" : normalized.metadata.notes,
            inputsConfirmed: mode === "apply" ? false : normalized.metadata.inputsConfirmed,
          },
        };
        snapshotRef.current = fresh;
        setSnapshot(fresh);
        setCurrent(null);
        setMarketSourceWorkspaceId(fresh.schemaVersion === 2 ? workspace.id : null);
        setDirty(true);
        resetHistory();
        setStatus({ tone: "ok", text: mode === "apply" ? `Applied preset “${workspace.name}” to a fresh quote.` : `Created an unsaved copy of “${workspace.name}”.` });
        if (mode === "duplicate") {
          setSaveDialog({
            kind: workspace.kind,
            initialName: `Copy of ${workspace.name}`,
            initialVisibility: workspace.visibility,
            sourceWorkspaceId: fresh.schemaVersion === 2 ? workspace.id : null,
          });
        }
      }
    } catch (error) {
      if (generation === sheetGeneration.current) setStatus({ tone: "error", text: errorMessage(error) });
    } finally {
      if (workspaceLoadGeneration.current === generation) {
        workspaceLoadGeneration.current = null;
        setLoadingWorkspaceId(null);
      }
    }
  }, [dirty, resetHistory, savePending]);

  const newSheet = async () => {
    if (savePending) return;
    if (dirty && !window.confirm("Start a new quote? The current unsaved sheet will be replaced.")) return;
    const generation = ++sheetGeneration.current;
    awaitingFreshFx.current = false;
    hydratedDraft.current = true;
    workspaceLoadGeneration.current = null;
    setLoadingWorkspaceId(null);
    setStatus({ tone: "muted", text: "Loading the latest confirmed EUR/RON curve…" });
    let curve: FxCurveDetail | null = null;
    let loadError: unknown = null;
    if (companyScopeId != null) {
      const result = await latestFxQuery.refetch();
      loadError = result.error;
      curve = loadError ? null : result.data ?? null;
    }
    if (generation !== sheetGeneration.current) {
      if (!currentRef.current) setStatus({ tone: "muted", text: "Kept the current sheet after the FX request finished." });
      return;
    }
    const next = createDefaultQuoteSnapshot(curve ? quoteFxCurveSnapshot(curve) : null);
    snapshotRef.current = next;
    setSnapshot(next);
    setCurrent(null);
    setMarketSourceWorkspaceId(null);
    setDirty(false);
    resetHistory();
    setStatus(loadError
      ? { tone: "error", text: `Started a fresh quote with blank FX because the confirmed central curve could not be loaded: ${errorMessage(loadError)}` }
      : curve
        ? { tone: "muted", text: `Started a fresh quote using confirmed EUR/RON curve ${curve.curveDate} · version ${curve.version} · ${curve.source}.` }
        : { tone: "muted", text: "Started a fresh quote with blank FX because no confirmed EUR/RON curve is available." });
  };

  const setMonthRange = (startMonth: string, endMonth: string) => {
    let months: string[];
    try {
      months = eexQuoteMonthRange(startMonth, endMonth);
    } catch (error) {
      setStatus({ tone: "error", text: errorMessage(error) });
      return;
    }
    if (months.length === 0 || months.length > 240) {
      setStatus({ tone: "error", text: "A quote horizon must contain between 1 and 240 delivery months." });
      return;
    }
    const previous = new Map(snapshotRef.current.rows.map((row) => [row.month, row]));
    const fxByMonth = new Map(
      snapshotRef.current.fxCurve?.points.map((point) => [point.deliveryMonth, point.mid]) ?? []
    );
    const rows = months.map((month) =>
      previous.get(month) ?? blankQuoteRow(month, snapshotRef.current.settings.defaultRisk, fxByMonth.get(month) ?? null)
    );
    replaceEconomicSnapshot({ ...snapshotRef.current, rows });
  };

  const copyTicket = async () => {
    const issue = quoteConfirmationIssue(snapshotRef.current);
    if (!snapshotRef.current.metadata.inputsConfirmed || issue) {
      setStatus({ tone: "error", text: issue ?? "Confirm the source, as-of date, and market inputs before copying a quote ticket." });
      return;
    }
    if (!snapshotRef.current.rows.some(eexQuoteRowIsActive)) {
      setStatus({ tone: "error", text: "Enter a non-zero MW profile before copying a quote ticket." });
      return;
    }
    const text = quoteTicket(snapshotRef.current, book);
    try {
      await navigator.clipboard.writeText(text);
      setStatus({ tone: "ok", text: "Copied a clean two-sided quote ticket to the clipboard." });
    } catch {
      setStatus({ tone: "error", text: "Clipboard access was denied by the browser." });
    }
  };

  const copyComponentTicket = async (component: QuoteComponent) => {
    const issue = quoteConfirmationIssue(snapshotRef.current);
    if (!snapshotRef.current.metadata.inputsConfirmed || issue) {
      setStatus({ tone: "error", text: issue ?? "Confirm the market inputs before copying a named strip quote." });
      return;
    }
    const componentMonths = new Set(component.months);
    const componentRows = snapshotRef.current.rows.filter((row) => componentMonths.has(row.month));
    if (componentRows.length !== componentMonths.size) {
      setStatus({ tone: "error", text: `Load a curve covering every month in “${component.name}” before copying it.` });
      return;
    }
    if (component.mw === 0) {
      setStatus({ tone: "error", text: `Enter a non-zero MW volume for “${component.name}” before copying it.` });
      return;
    }
    if (!componentRows.every(eexQuoteRowHasMarketInputs)) {
      setStatus({ tone: "error", text: `Complete HU, quoted basis, risk, and positive FX for every month in “${component.name}”.` });
      return;
    }
    const componentBook = computeEexQuoteBook(
      componentRows.map((row) => ({ ...row, mw: component.mw })),
      snapshotRef.current.settings
    );
    const text = [
      component.name,
      `${component.months.length} delivery months · ${component.mw} MW flat`,
      `Market data as of: ${snapshotRef.current.metadata.asOf}`,
      `Input source: ${snapshotRef.current.metadata.marketDataSource || "Manual / unspecified"}`,
      ...fxProvenanceLines(snapshotRef.current),
      `Volume: ${formatMetric(componentBook.totals.totalMwh, 0)} MWh`,
      `Physical RON/MWh — buy ${formatMetric(componentBook.totals.buyRon, snapshotRef.current.settings.priceDecimals)} / sell ${formatMetric(componentBook.totals.sellRon, snapshotRef.current.settings.priceDecimals)}`,
      `Physical EUR/MWh — buy ${formatMetric(componentBook.totals.buyEur, snapshotRef.current.settings.priceDecimals)} / sell ${formatMetric(componentBook.totals.sellEur, snapshotRef.current.settings.priceDecimals)}`,
    ].join("\n");
    try {
      await navigator.clipboard.writeText(text);
      setStatus({ tone: "ok", text: `Copied named strip “${component.name}”.` });
    } catch {
      setStatus({ tone: "error", text: "Clipboard access was denied by the browser." });
    }
  };

  const saveError = createMutation.isError ? errorMessage(createMutation.error) : null;

  return (
    <div className="operations-workspace ops-quotes flex h-[calc(100vh-3.5rem)] min-h-[600px] flex-col overflow-hidden bg-bg">
      <div className="ops-quote-heading border-b border-border/70 bg-bg px-3 py-2">
        <div className="flex flex-wrap items-center gap-2">
          <div className="mr-2 flex items-center gap-2">
            <div className="grid h-8 w-8 place-items-center rounded-md bg-accent/10 text-accent"><Sparkles className="h-4 w-4" /></div>
            <div>
              <div className="flex items-center gap-2"><h1 className="text-sm font-semibold">Quote Builder</h1>{dirty && <span className="h-1.5 w-1.5 rounded-full bg-warn-orange" title="Unsaved changes" />}</div>
              <div className="text-[10px] text-muted">RO physical quote · frozen central FX snapshot · calc v1</div>
            </div>
          </div>
          <button type="button" onClick={newSheet} disabled={!canWrite || loadingWorkspaceId !== null || savePending} className="quote-page-action"><FilePlus2 className="h-3.5 w-3.5" />New</button>
          <button type="button" onClick={() => setSidePanel(sidePanel === "library" ? null : "library")} className={cn("quote-page-action", sidePanel === "library" && "border-accent text-accent")}><Library className="h-3.5 w-3.5" />Library <span className="rounded bg-border/50 px-1 text-[9px]">{workspaces.length}</span></button>
          <button type="button" onClick={() => setSidePanel(sidePanel === "components" ? null : "components")} className={cn("quote-page-action", sidePanel === "components" && "border-accent text-accent")}><Layers3 className="h-3.5 w-3.5" />Strips <span className="rounded bg-border/50 px-1 text-[9px]">{snapshot.components.length}</span></button>
          {marketSnapshotAvailability.data?.available === true && canWrite && (
            <button
              type="button"
              onClick={() => {
                if (selectedMonths.length === 0) {
                  setStatus({ tone: "error", text: "Select one or more delivery-month rows before previewing EEX market values." });
                  return;
                }
                setMarketSnapshotOpen(true);
              }}
              disabled={loadingWorkspaceId !== null || savePending}
              className="quote-page-action border-accent/50 text-accent"
              title="Preview and atomically apply cached EEX market values to the selected months"
            >
              <Sparkles className="h-3.5 w-3.5" />Market snapshot
            </button>
          )}
          <button type="button" onClick={() => setSidePanel(sidePanel === "customize" ? null : "customize")} className={cn("quote-page-action", sidePanel === "customize" && "border-accent text-accent")}><SlidersHorizontal className="h-3.5 w-3.5" />Customize</button>
          <button type="button" onClick={() => void copyTicket()} disabled={!mainQuoteReady} title={mainQuoteReady ? "Copy quote ticket" : confirmationIssue ?? "Enter a non-zero MW profile and confirm market inputs before copying"} className="quote-page-action"><Copy className="h-3.5 w-3.5" />Copy ticket</button>
          <button
            type="button"
            disabled={!canWrite || loadingWorkspaceId !== null || (snapshot.metadata.inputsConfirmed && confirmationIssue == null)}
            onClick={() => {
              const issue = quoteConfirmationIssue(snapshotRef.current);
              if (issue) {
                setStatus({ tone: "error", text: issue });
                return;
              }
              replaceSnapshot({ ...snapshotRef.current, metadata: { ...snapshotRef.current.metadata, inputsConfirmed: true } }, { history: false });
            }}
            className={cn("quote-page-action", snapshot.metadata.inputsConfirmed && confirmationIssue == null ? "border-pos-green/50 text-pos-green" : "border-warn-orange/60 text-warn-orange")}
            title={snapshot.metadata.inputsConfirmed && confirmationIssue == null ? "Market inputs confirmed by trader" : confirmationIssue ?? "Confirm the source, as-of date, and input curve"}
          >
            {snapshot.metadata.inputsConfirmed && confirmationIssue == null ? <CheckCircle2 className="h-3.5 w-3.5" /> : <TriangleAlert className="h-3.5 w-3.5" />}
            {snapshot.metadata.inputsConfirmed && confirmationIssue == null ? "Inputs confirmed" : "Confirm inputs"}
          </button>
          <div className="ml-auto flex items-center gap-1">
            {canWrite && (
              <>
                <button type="button" disabled={loadingWorkspaceId !== null || savePending} onClick={() => setSaveDialog({ kind: "preset", initialName: `${snapshot.metadata.title} preset`, initialVisibility: "private", sourceWorkspaceId: snapshot.schemaVersion === 2 ? marketSourceWorkspaceId : null })} className="quote-page-action">Save preset</button>
                <button type="button" disabled={loadingWorkspaceId !== null || savePending} onClick={() => setSaveDialog({ kind: "quote", initialName: current ? `Copy of ${current.name}` : snapshot.metadata.title, initialVisibility: current?.visibility ?? "private", sourceWorkspaceId: snapshot.schemaVersion === 2 ? marketSourceWorkspaceId : null })} className="quote-page-action">Save as…</button>
                <button type="button" onClick={saveCurrent} disabled={loadingWorkspaceId !== null || updateMutation.isPending || createMutation.isPending} className="inline-flex h-8 items-center gap-1.5 rounded bg-accent px-3 text-xs font-medium text-white hover:brightness-105 disabled:opacity-40">
                  <Save className="h-3.5 w-3.5" />{updateMutation.isPending ? "Saving…" : current && canManageCurrent ? `Save v${current.version + 1}` : "Save quote"}
                </button>
              </>
            )}
            <button type="button" onClick={() => setSidePanel(sidePanel ? null : "customize")} className="ml-1 rounded p-1.5 text-muted hover:bg-accent/10 hover:text-fg" title={sidePanel ? "Close panel" : "Open panel"}>{sidePanel ? <PanelRightClose className="h-4 w-4" /> : <PanelRightOpen className="h-4 w-4" />}</button>
          </div>
        </div>

        <div className="ops-quote-meta mt-2 grid grid-cols-[minmax(14rem,1.5fr)_minmax(9rem,1fr)_minmax(11rem,1fr)_9rem_8rem] gap-2">
          <Field label="Quote title"><input value={snapshot.metadata.title} disabled={!canWrite || loadingWorkspaceId !== null} maxLength={120} onChange={(event) => replaceSnapshot({ ...snapshotRef.current, metadata: { ...snapshotRef.current.metadata, title: event.target.value } }, { history: false })} className="quote-meta-input" /></Field>
          <Field label="Counterparty"><input value={snapshot.metadata.counterparty} disabled={!canWrite || loadingWorkspaceId !== null} maxLength={120} placeholder="Optional" onChange={(event) => replaceSnapshot({ ...snapshotRef.current, metadata: { ...snapshotRef.current.metadata, counterparty: event.target.value } }, { history: false })} className="quote-meta-input" /></Field>
          <Field label="Input source"><input value={snapshot.metadata.marketDataSource} disabled={!canWrite || loadingWorkspaceId !== null || quoteHasFrozenMarketSource(snapshot)} title={quoteHasFrozenMarketSource(snapshot) ? "Frozen by the applied EEX market snapshot" : undefined} maxLength={120} onChange={(event) => replaceEconomicSnapshot({ ...snapshotRef.current, metadata: { ...snapshotRef.current.metadata, marketDataSource: event.target.value } }, { history: false })} className="quote-meta-input" /></Field>
          <Field label="Market data as of"><input type="date" value={snapshot.metadata.asOf} disabled={!canWrite || loadingWorkspaceId !== null || quoteHasFrozenMarketSource(snapshot)} title={quoteHasFrozenMarketSource(snapshot) ? "Frozen by the applied EEX market snapshot" : undefined} onChange={(event) => { if (event.target.value) replaceEconomicSnapshot({ ...snapshotRef.current, metadata: { ...snapshotRef.current.metadata, asOf: event.target.value } }, { history: false }); }} className="quote-meta-input" /></Field>
          <Field label="Valid for"><div className="flex h-8 items-center rounded border border-border focus-within:border-accent"><input type="number" min={0} max={10080} value={snapshot.metadata.validityMinutes} disabled={!canWrite || loadingWorkspaceId !== null} onChange={(event) => { const value = Number(event.target.value); if (Number.isFinite(value)) replaceSnapshot({ ...snapshotRef.current, metadata: { ...snapshotRef.current.metadata, validityMinutes: Math.max(0, Math.min(10080, Math.round(value))) } }, { history: false }); }} className="min-w-0 flex-1 bg-transparent px-2 text-right font-mono text-xs outline-none" /><span className="pr-2 text-[9px] text-muted">min</span></div></Field>
        </div>
      </div>

      <QuoteSummary book={book} />

      <FxProvenanceBanner snapshot={snapshot} overrideMonths={fxOverrideMonths} />

      {snapshot.schemaVersion === 2 && <MarketProvenanceBanner snapshot={snapshot} />}

      {status && (
        <div className={cn("flex h-7 items-center border-b px-3 text-[11px]", status.tone === "error" ? "border-neg-red/30 bg-neg-red/5 text-neg-red" : status.tone === "ok" ? "border-pos-green/30 bg-pos-green/5 text-pos-green" : "border-border/60 bg-bg text-muted")}>
          {status.text}
          <button type="button" onClick={() => setStatus(null)} className="ml-auto px-1 text-current opacity-60 hover:opacity-100">×</button>
        </div>
      )}

      <div className="flex min-h-0 flex-1">
        <div className="flex min-w-0 flex-1 flex-col px-3 pb-3 pt-2">
          <QuoteGrid
            snapshot={snapshot}
            book={book}
            canEdit={canWrite && loadingWorkspaceId === null}
            canUndo={historyState.canUndo}
            canRedo={historyState.canRedo}
            onRowsChange={(rows) => replaceEconomicSnapshot({ ...snapshotRef.current, rows })}
            onLayoutChange={(layout) => replaceSnapshot({ ...snapshotRef.current, layout })}
            onSelectedMonthsChange={setSelectedMonths}
            onUndo={undo}
            onRedo={redo}
            onSave={saveCurrent}
            onOpenShortcuts={() => setShortcutOpen(true)}
          />
        </div>
        {sidePanel && (
          <QuoteSidebar
            panel={sidePanel}
            snapshot={snapshot}
            workspaces={workspaces}
            currentId={current?.id ?? null}
            userId={user?.userId ?? null}
            canWrite={canWrite && loadingWorkspaceId === null && !savePending}
            isAdmin={isAdmin}
            loading={workspacesQuery.isLoading || loadingWorkspaceId !== null || savePending}
            selectedMonths={selectedMonths}
            onClose={() => setSidePanel(null)}
            onLoad={(workspace) => void loadWorkspace(workspace, "open")}
            onApplyPreset={(workspace) => void loadWorkspace(workspace, "apply")}
            onDuplicate={(workspace) => void loadWorkspace(workspace, "duplicate")}
            onDelete={(workspace) => { if (window.confirm(`Delete “${workspace.name}”?`)) deleteMutation.mutate(workspace); }}
            onAddComponent={(component) => replaceSnapshot({
              ...snapshotRef.current,
              components: [...snapshotRef.current.components, component],
            })}
            onUpdateComponent={(component) => replaceSnapshot({
              ...snapshotRef.current,
              components: snapshotRef.current.components.map((item) => item.id === component.id ? component : item),
            })}
            onDeleteComponent={(component) => replaceSnapshot({
              ...snapshotRef.current,
              components: snapshotRef.current.components.filter((item) => item.id !== component.id),
            })}
            onApplyComponent={(component) => {
              const months = new Set(component.months);
              replaceEconomicSnapshot({
                ...snapshotRef.current,
                metadata: { ...snapshotRef.current.metadata, title: component.name },
                rows: snapshotRef.current.rows.map((row) => ({
                  ...row,
                  mw: months.has(row.month) ? component.mw : 0,
                })),
              });
              setStatus({ tone: "ok", text: `Loaded “${component.name}” as the active monthly MW profile.` });
            }}
            onCopyComponent={(component) => void copyComponentTicket(component)}
            onSettingsChange={(settings) => replaceEconomicSnapshot({ ...snapshotRef.current, settings })}
            onApplyDefaultRisk={(overwrite) => replaceEconomicSnapshot({
              ...snapshotRef.current,
              rows: snapshotRef.current.rows.map((row) =>
                overwrite || row.risk == null
                  ? { ...row, risk: snapshotRef.current.settings.defaultRisk }
                  : row
              ),
            })}
            onLayoutChange={(layout) => replaceSnapshot({ ...snapshotRef.current, layout })}
            onNotesChange={(notes) => replaceSnapshot({ ...snapshotRef.current, metadata: { ...snapshotRef.current.metadata, notes } }, { history: false })}
            onSetRange={setMonthRange}
            onResetLayout={() => replaceSnapshot({ ...snapshotRef.current, layout: { ...DEFAULT_QUOTE_LAYOUT, columnOrder: [...DEFAULT_QUOTE_LAYOUT.columnOrder], hiddenColumns: [], columnWidths: {} } })}
          />
        )}
      </div>

      {saveDialog && (
        <SaveWorkspaceDialog
          kind={saveDialog.kind}
          initialName={saveDialog.initialName}
          initialVisibility={saveDialog.initialVisibility}
          busy={createMutation.isPending}
          error={saveError}
          inputsConfirmed={snapshot.metadata.inputsConfirmed && confirmationIssue == null}
          onCancel={() => { if (!createMutation.isPending) { createMutation.reset(); setSaveDialog(null); } }}
          onSave={({ name, visibility }) => {
            const toSave = saveDialog.kind === "quote"
              ? { ...snapshotRef.current, metadata: { ...snapshotRef.current.metadata, title: name } }
              : snapshotRef.current;
            if (saveDialog.kind === "quote") {
              snapshotRef.current = toSave;
              setSnapshot(toSave);
            }
            createMutation.mutate({
              kind: saveDialog.kind,
              name,
              visibility,
              snapshot: toSave,
              sourceWorkspaceId: saveDialog.sourceWorkspaceId,
            });
          }}
        />
      )}
      {shortcutOpen && <ShortcutDialog onClose={() => setShortcutOpen(false)} />}
      {marketSnapshotOpen && (
        <QuoteMarketSnapshotDialog
          snapshot={snapshot}
          workspace={current && canManageCurrent ? { id: current.id, version: current.version } : null}
          months={[...selectedMonths].sort()}
          onClose={() => setMarketSnapshotOpen(false)}
          onApplied={(applied) => {
            replaceSnapshot(applied);
            // The newly applied provenance no longer matches any previously
            // loaded workspace. Its current server signature is sufficient.
            setMarketSourceWorkspaceId(null);
            setMarketSnapshotOpen(false);
            setStatus({ tone: "ok", text: `Applied cached EEX ${applied.schemaVersion === 2 ? applied.marketSnapshot.selectedPolicy : "market"} values to ${selectedMonths.length} month${selectedMonths.length === 1 ? "" : "s"}. Confirm the updated inputs before copying or saving a final quote.` });
          }}
        />
      )}
    </div>
  );
}

function summaryOf(workspace: QuoteWorkspaceRecord): CurrentWorkspace {
  const { snapshot: _snapshot, ...summary } = workspace;
  return summary;
}

function isUuid(value: unknown): value is string {
  return typeof value === "string" &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
}

function blankQuoteRow(month: string, risk: number, fx: number | null): EexQuoteRowInput {
  return {
    id: month,
    month,
    hours: eexQuoteMonthHours(month),
    mw: 0,
    huPrice: null,
    quotedSpread: null,
    risk,
    fx,
  };
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <label className="min-w-0 space-y-0.5 text-[9px] font-medium uppercase tracking-[0.08em] text-muted"><span>{label}</span>{children}</label>;
}

function FxProvenanceBanner({ snapshot, overrideMonths }: { snapshot: QuoteSnapshot; overrideMonths: string[] }) {
  const curve = snapshot.fxCurve;
  if (!curve) {
    return (
      <div className="flex min-h-7 items-center gap-2 border-b border-warnOrange/30 bg-warnOrange/5 px-3 py-1.5 text-[10px] text-warnOrange">
        <TriangleAlert className="h-3.5 w-3.5 shrink-0" />
        FX: manual / legacy snapshot · EUR/RON (RON per EUR). No confirmed central-curve provenance is attached.
      </div>
    );
  }
  return (
    <div className={cn(
      "flex min-h-7 flex-wrap items-center gap-x-2 gap-y-1 border-b px-3 py-1.5 text-[10px]",
      overrideMonths.length > 0 ? "border-warnOrange/30 bg-warnOrange/5" : "border-posGreen/30 bg-posGreen/5"
    )}>
      <CheckCircle2 className={cn("h-3.5 w-3.5 shrink-0", overrideMonths.length > 0 ? "text-warnOrange" : "text-posGreen")} />
      <span className="font-medium">EUR/RON mid · RON per EUR</span>
      <span>curve date <span className="font-mono">{curve.curveDate}</span></span>
      <span>version <span className="font-mono">{curve.curveVersion}</span></span>
      <span>source <span className="font-medium">{curve.source}</span></span>
      <span title={curve.curveId}>ID <span className="font-mono">{shortId(curve.curveId)}</span></span>
      <span className="text-muted">applied {formatTimestamp(curve.appliedAt)}</span>
      {overrideMonths.length > 0 && (
        <span className="font-medium text-warnOrange" title={overrideMonths.join(", ")}>
          {overrideMonths.length} manual FX override{overrideMonths.length === 1 ? "" : "s"}; saved row values are authoritative
        </span>
      )}
    </div>
  );
}

function MarketProvenanceBanner({ snapshot }: { snapshot: Extract<QuoteSnapshot, { schemaVersion: 2 }> }) {
  const application = snapshot.marketSnapshot;
  const huOverrides = application.rows.filter((row) => row.huPriceManualOverride).length;
  const basisOverrides = application.rows.filter((row) => row.quotedSpreadManualOverride).length;
  const overrideCount = huOverrides + basisOverrides;
  return (
    <div className={cn(
      "flex min-h-7 flex-wrap items-center gap-x-2 gap-y-1 border-b px-3 py-1.5 text-[10px]",
      overrideCount > 0 ? "border-warnOrange/30 bg-warnOrange/5" : "border-posGreen/30 bg-posGreen/5"
    )}>
      {overrideCount > 0 ? <TriangleAlert className="h-3.5 w-3.5 shrink-0 text-warnOrange" /> : <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-posGreen" />}
      <span className="font-medium">EEX cached market snapshot · {application.selectedPolicy}</span>
      <span>{application.rows.length} month{application.rows.length === 1 ? "" : "s"}</span>
      <span>RO−HU basis {application.basisApplied ? "applied" : "observed only"}</span>
      <span>as of <span className="font-mono">{application.asOf}</span></span>
      <span className="text-muted">applied {formatTimestamp(application.appliedAt)}</span>
      <span title={application.snapshotId}>ID <span className="font-mono">{shortId(application.snapshotId)}</span></span>
      {overrideCount > 0 && (
        <span className="font-medium text-warnOrange">
          {overrideCount} manual market-value override{overrideCount === 1 ? "" : "s"}; confirmation reset
        </span>
      )}
    </div>
  );
}

function QuoteSummary({ book }: { book: ReturnType<typeof computeEexQuoteBook> }) {
  const buyEffectiveFx = ratio(book.totals.buyRon, book.totals.buyEur);
  const sellEffectiveFx = ratio(book.totals.sellRon, book.totals.sellEur);
  return (
    <div className="grid grid-cols-5 border-b border-border/70 bg-bg">
      <SummaryCell label="Quoted energy" value={formatMetric(book.totals.totalMwh, 0)} unit="MWh" detail="Actual delivery hours × MW" />
      <SummaryCell label="Physical RON" value={`${formatMetric(book.totals.buyRon, 2)} / ${formatMetric(book.totals.sellRon, 2)}`} unit="buy / sell" detail="Volume-weighted flat quote" accent />
      <SummaryCell label="Physical EUR" value={`${formatMetric(book.totals.buyEur, 2)} / ${formatMetric(book.totals.sellEur, 2)}`} unit="buy / sell" detail="Volume-weighted flat quote" />
      <SummaryCell label="Effective EUR/RON" value={`${formatMetric(buyEffectiveFx, 4)} / ${formatMetric(sellEffectiveFx, 4)}`} unit="RON per EUR · buy / sell" detail="Physical RON ÷ physical EUR" />
      <SummaryCell label="Gross shape exposure" value={formatMetric(book.totals.grossShapePnlRon / 2, 0)} unit="RON each side" detail="Monthly deviations around flat quote" />
    </div>
  );
}

function SummaryCell({ label, value, unit, detail, accent = false }: { label: string; value: string; unit: string; detail: string; accent?: boolean }) {
  return (
    <div className={cn("min-w-0 border-r border-border/70 px-3 py-2 last:border-r-0", accent && "bg-[#d0beff]/30 dark:bg-[#40325f]/40")}>
      <div className="text-[9px] font-semibold uppercase tracking-[0.1em] text-muted">{label}</div>
      <div className="mt-0.5 truncate font-mono text-base font-semibold tabular-nums" title={value}>{value}</div>
      <div className="flex items-center justify-between gap-2 text-[9px] text-muted"><span>{unit}</span><span className="truncate">{detail}</span></div>
    </div>
  );
}

function ratio(a: number | null, b: number | null): number | null {
  return a != null && b != null && b !== 0 ? a / b : null;
}

function formatMetric(value: number | null, decimals: number): string {
  if (value == null || !Number.isFinite(value)) return "—";
  return new Intl.NumberFormat("en-GB", { minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(value);
}

function quoteTicket(snapshot: QuoteSnapshot, book: ReturnType<typeof computeEexQuoteBook>): string {
  const lines = [
    snapshot.metadata.title || "EEX quote",
    snapshot.metadata.counterparty ? `Counterparty: ${snapshot.metadata.counterparty}` : null,
    `Market data as of: ${snapshot.metadata.asOf}`,
    `Input source: ${snapshot.metadata.marketDataSource || "Manual / unspecified"}`,
    ...fxProvenanceLines(snapshot),
    `Inputs confirmed: ${snapshot.metadata.inputsConfirmed ? "yes" : "NO"}`,
    `Validity: ${snapshot.metadata.validityMinutes} minutes`,
    `Volume: ${formatMetric(book.totals.totalMwh, 0)} MWh`,
    `Physical RON/MWh — buy ${formatMetric(book.totals.buyRon, snapshot.settings.priceDecimals)} / sell ${formatMetric(book.totals.sellRon, snapshot.settings.priceDecimals)}`,
    `Physical EUR/MWh — buy ${formatMetric(book.totals.buyEur, snapshot.settings.priceDecimals)} / sell ${formatMetric(book.totals.sellEur, snapshot.settings.priceDecimals)}`,
    snapshot.metadata.notes ? `Notes: ${snapshot.metadata.notes}` : null,
  ];
  return lines.filter((line): line is string => Boolean(line)).join("\n");
}

function fxProvenanceLines(snapshot: QuoteSnapshot): string[] {
  if (!snapshot.fxCurve) {
    return ["FX convention: EUR/RON (RON per EUR)", "FX provenance: manual / legacy snapshot"];
  }
  const overrides = quoteFxOverrideMonths(snapshot);
  return [
    "FX convention: EUR/RON (RON per EUR)",
    `FX curve: ${snapshot.fxCurve.curveDate} · version ${snapshot.fxCurve.curveVersion} · ${snapshot.fxCurve.source} · ${snapshot.fxCurve.curveId} · mid`,
    overrides.length > 0
      ? `FX overrides: ${overrides.join(", ")} (saved row values are authoritative)`
      : "FX overrides: none",
  ];
}

function shortId(value: string): string {
  return value.length > 12 ? `${value.slice(0, 8)}…` : value;
}

function quoteConfirmationIssue(snapshot: QuoteSnapshot): string | null {
  if (!snapshot.metadata.marketDataSource.trim()) {
    return "Identify the market-data source before confirming these inputs.";
  }
  const incomplete = snapshot.rows.filter(
    (row) => eexQuoteRowIsActive(row) && !eexQuoteRowHasMarketInputs(row)
  );
  if (incomplete.length > 0) {
    const labels = incomplete.slice(0, 3).map((row) => row.month).join(", ");
    const remainder = incomplete.length > 3 ? ` and ${incomplete.length - 3} more` : "";
    return `Complete HU, quoted basis, risk, and positive FX for active month${incomplete.length === 1 ? "" : "s"}: ${labels}${remainder}.`;
  }
  return null;
}

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return String(error);
}

function formatTimestamp(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("en-GB", { dateStyle: "medium", timeStyle: "short" }).format(date);
}
